import { useState, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Label,
    FormGroup,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { forwardInspection } from './FormikHelpers/Api';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_RECIPIENTS = 10;

const parseRecipients = (raw) => {
    return raw
        .split(/[\n,;]+/)
        .map(s => s.trim())
        .filter(Boolean);
};

const dedup = (arr) => {
    const seen = new Set();
    return arr.filter(e => {
        const k = e.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
    });
};

const ForwardInspectionModal = ({ open, onClose, inspectionId }) => {
    const [recipientsRaw, setRecipientsRaw] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setRecipientsRaw('');
            setMessage('');
            setSending(false);
        }
    }, [open]);

    const tokens = dedup(parseRecipients(recipientsRaw));
    const invalid = tokens.filter(t => !EMAIL_RE.test(t));
    const valid = tokens.filter(t => EMAIL_RE.test(t));
    const overLimit = tokens.length > MAX_RECIPIENTS;
    const canSend = tokens.length > 0 && invalid.length === 0 && !overLimit && !sending;

    const handleSend = async () => {
        if (!canSend) return;
        setSending(true);
        try {
            const result = await forwardInspection(inspectionId, {
                recipients: valid,
                ...(message.trim() ? { message: message.trim() } : {}),
            });
            const { sent = 0, skipped = 0, embedded = 0, attached = 0, linked = 0 } = result || {};
            const summary = [
                `Forwarded to ${sent} recipient(s)`,
                `${embedded} photo(s) embedded, ${attached} file(s) attached`,
                linked ? `${linked} large file(s) linked` : null,
                skipped ? `${skipped} skipped` : null,
            ].filter(Boolean).join(' · ');
            toast.success(summary);
            onClose();
        } catch {
        } finally {
            setSending(false);
        }
    };

    const hasValidationMsg = invalid.length > 0 || overLimit;

    return (
        <Modal isOpen={open} toggle={onClose} centered className="suite-inspection-dialog">
            <ModalHeader toggle={onClose}>Forward Inspection via Email</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label htmlFor="fim-recipients">
                        Recipients{' '}
                        <span style={{ fontWeight: 400, color: tokens.length > MAX_RECIPIENTS ? '#DC2626' : '#64748B', fontSize: '12px' }}>
                            {tokens.length}/{MAX_RECIPIENTS}
                        </span>
                    </Label>
                    <textarea
                        id="fim-recipients"
                        className="form-control"
                        rows={3}
                        placeholder="Enter email addresses separated by comma, semicolon, or newline"
                        value={recipientsRaw}
                        onChange={e => setRecipientsRaw(e.target.value)}
                        disabled={sending}
                        autoFocus
                        aria-describedby={hasValidationMsg ? 'fim-validation' : undefined}
                        style={{ resize: 'vertical', fontSize: '16px', minHeight: 88 }}
                    />
                    {hasValidationMsg && (
                        <div id="fim-validation" style={{ marginTop: 4, fontSize: '12px', color: '#DC2626' }}>
                            {overLimit && <div>Maximum {MAX_RECIPIENTS} recipients allowed.</div>}
                            {invalid.length > 0 && (
                                <div>Invalid: {invalid.join(', ')}</div>
                            )}
                        </div>
                    )}
                </FormGroup>
                <FormGroup className="mb-0">
                    <Label htmlFor="fim-message">Message <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: '12px' }}>(optional)</span></Label>
                    <textarea
                        id="fim-message"
                        className="form-control"
                        rows={3}
                        placeholder="Add a personal note to include with the inspection report…"
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        disabled={sending}
                        maxLength={2000}
                        style={{ resize: 'vertical', fontSize: '16px', minHeight: 88 }}
                    />
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button outline color="secondary" onClick={onClose} disabled={sending} style={{ minHeight: 44 }}>
                    Cancel
                </Button>
                <Button
                    color="primary"
                    onClick={handleSend}
                    disabled={!canSend}
                    aria-busy={sending}
                    style={{ minHeight: 44 }}
                >
                    {sending && <Spinner size="sm" className="me-2" />}
                    {sending ? 'Sending...' : 'Send'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export { ForwardInspectionModal };
